import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) toast.error(typeof data.detail === "string" ? data.detail : "Reset failed");
      else setDone(true);
    } catch {
      toast.error("Network error. Please try again.");
    }
    setBusy(false);
  };

  const inputCls = "w-full rounded-2xl border border-ink/15 bg-cream px-5 py-3.5 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35";

  return (
    <div className="min-h-screen bg-paper text-ink font-body flex items-center justify-center px-5" data-testid="reset-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-sm rounded-[2rem] bg-cream border border-ink/10 p-8">
        <Link to="/" className="flex items-center gap-2.5 mb-8" data-testid="reset-home-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span></span>
        </Link>
        {done ? (
          <div data-testid="reset-success">
            <CheckCircle2 className="h-10 w-10 text-iqgreen" />
            <h1 className="font-display font-bold text-2xl tracking-tight mt-4">Password updated</h1>
            <p className="mt-2 text-sm text-ink/60">You're all set — log in with your new password.</p>
            <Link to="/assessment" data-testid="reset-login-link" className="mt-6 inline-flex bg-ink text-paper font-semibold px-7 py-3.5 rounded-full hover:bg-iqblue transition-colors">
              Continue to log in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display font-bold text-2xl tracking-tight">Choose a new password</h1>
            <p className="mt-2 text-sm text-ink/60">Minimum 6 characters. The link expires 60 minutes after it was sent.</p>
            {!token && <p className="mt-4 text-sm text-iqpink font-semibold" data-testid="reset-no-token">This reset link is missing its token. Please request a fresh one.</p>}
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="relative">
                <input data-testid="reset-password-input" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" required minLength={6} className={`${inputCls} pr-12`} />
                <button type="button" onClick={() => setShowPw(!showPw)} data-testid="reset-password-toggle" className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink transition-colors" aria-label="Toggle password visibility">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <input data-testid="reset-confirm-input" type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" required minLength={6} className={inputCls} />
              <button type="submit" disabled={busy || !token} data-testid="reset-submit-button" className="w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-4 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors duration-300">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
